import { Helmet } from 'react-helmet';

import * as types from 'types';
import { RadioOptionsWithConfirm, SelectWithConfirm } from 'shared-components';

import QuestionPage from '../../../templates/question';
import { Q } from '../../../../store/student/types';

import { ConfigField } from '../../../../types';
import { partnerVisaStatusLabel } from './utils';
import { titleAppend } from '../../../../utilities/head';
import { useDispatch, useSelector } from 'react-redux';
import * as StudentActions from '../../../../store/student/actions';
import * as OfferActions from '../../../../store/student/offer/actions';
import {
  createNavigateForwardAction,
  createNavigateBackwardAction,
  createNavigateToAction
} from '../../../../middleware/actions';
import { Pages } from '../../../../store/student/history/types';
import { toggleTaskMenu } from '../../../../store/ui/actions';
import { ApplicationState } from '../../../../store';

const toBool = (val?: boolean) => (val === undefined ? true : val);

const useData = () => {
  const sectionTitle = useSelector((state: ApplicationState) => state.config.Checklist.History.shortTitle);
  const config = useSelector((state: ApplicationState) => state.config);

  const {
    FamilyMembersTravelling: familyMembersTravellingConfig,
    SpouseTravelling: spouseTravellingConfig,
    PartnerVisaStatus: partnerVisaStatusConfig,
    ChildrenTravelling: childrenTravellingConfig,
    ChildrenTravellingNumber: childrenTravellingNumberConfig
  }: {
    FamilyMembersTravelling: ConfigField;
    SpouseTravelling: ConfigField;
    PartnerVisaStatus: ConfigField;
    ChildrenTravelling: ConfigField;
    ChildrenTravellingNumber: ConfigField;
  } = useSelector((state: ApplicationState) => state.config.Checklist.History.Page3);

  const {
    hasDependents,
    hasPartner,
    partnerVisaStatus = '' as types.PartnerVisaStatus,
    hasChildren,
    childrenNum = 0
  } = useSelector((state: ApplicationState) => state.student.offer);

  const familyMembersTravellingVisible = toBool(familyMembersTravellingConfig?.visible);
  const spouseTravellingVisible = toBool(spouseTravellingConfig?.visible);
  const partnerVisaStatusVisible = toBool(partnerVisaStatusConfig?.visible) && hasPartner === true;
  const childrenTravellingVisible = toBool(childrenTravellingConfig?.visible);
  const childrenTravellingNumberVisible = toBool(childrenTravellingNumberConfig?.visible) && hasChildren === true;

  const familyMembersTravellingMandatory = toBool(familyMembersTravellingConfig?.mandatory);
  const spouseTravellingMandatory = toBool(spouseTravellingConfig?.mandatory);
  const partnerVisaStatusMandatory = toBool(partnerVisaStatusConfig?.mandatory);
  const childrenTravellingMandatory = toBool(childrenTravellingConfig?.mandatory);
  const childrenTravellingNumberMandatory = toBool(childrenTravellingNumberConfig?.mandatory);

  const complete =
    (!familyMembersTravellingVisible || !familyMembersTravellingMandatory || hasDependents !== undefined) &&
    (!spouseTravellingVisible || !spouseTravellingMandatory || hasPartner !== undefined) &&
    (!partnerVisaStatusVisible ||
      !partnerVisaStatusMandatory ||
      partnerVisaStatus !== ('' as types.PartnerVisaStatus)) &&
    (!childrenTravellingVisible || !childrenTravellingMandatory || hasChildren !== undefined) &&
    (!childrenTravellingNumberVisible || !childrenTravellingNumberMandatory || Number(childrenNum) > 0);

  return {
    sectionTitle,
    complete,
    currentStep: 3,
    steps: 3,
    hasDependents,
    familyMembersTravellingVisible,
    familyMembersTravellingConfig,
    hasPartner,
    spouseTravellingVisible,
    spouseTravellingConfig,
    partnerVisaStatus,
    partnerVisaStatusVisible,
    partnerVisaStatusConfig,
    hasChildren,
    childrenTravellingVisible,
    childrenTravellingConfig,
    childrenNum: Number(childrenNum),
    childrenTravellingNumberVisible,
    childrenTravellingNumberConfig,
    config
  };
};

const HistoryQPage3 = () => {
  const {
    sectionTitle,
    complete,
    currentStep,
    steps,
    hasDependents,
    familyMembersTravellingVisible,
    familyMembersTravellingConfig,
    hasPartner,
    spouseTravellingVisible,
    spouseTravellingConfig,
    partnerVisaStatus,
    partnerVisaStatusVisible,
    partnerVisaStatusConfig,
    hasChildren,
    childrenTravellingVisible,
    childrenNum,
    childrenTravellingNumberVisible,
    childrenTravellingNumberConfig,
    config
  } = useData();

  const dispatch = useDispatch();
  const onPrevious = () => dispatch(createNavigateBackwardAction());
  const onNext = config => {
    dispatch(StudentActions.saveHistory(Pages.PAGE3, config));
    dispatch(createNavigateForwardAction());
  };
  const onSkip = () => dispatch(createNavigateForwardAction());
  const onChange = (fieldName: string, value) => dispatch(OfferActions.createUpdateOfferFieldAction(fieldName, value));
  const onBackToTaskList = () => {
    dispatch(createNavigateToAction('/dashboard'));
    dispatch(toggleTaskMenu(false));
  };

  const handleYesNoClick = (target: Q, question) => {
    onChange(target, question.char === 'Y' ? true : false);
  };

  const handlePartnerVisaStatusClick = question => {
    let status: types.PartnerVisaStatus;
    switch (question.char) {
      case '1':
        status = types.PartnerVisaStatus.APPLYING_DEPENDANT;
        break;
      case '2':
        status = types.PartnerVisaStatus.APPLYING_DIFFERENT;
        break;
      case '3':
        status = types.PartnerVisaStatus.READY_TO_TRAVEL_VALID;
        break;
      default:
        status = types.PartnerVisaStatus.ALREADY_IN_UK_VALID;
    }
    onChange(Q.PARTNER_VISA_STATUS, status);
  };

  return (
    <QuestionPage
      complete={complete}
      currentStep={currentStep}
      steps={steps}
      onNext={() => onNext(config)}
      onSkip={onSkip}
      onPrevious={onPrevious}
      onBackToTaskList={onBackToTaskList}
      section={sectionTitle}
    >
      <Helmet>
        <title>
          {`${sectionTitle}: Step ${currentStep}`} {titleAppend}
        </title>
      </Helmet>

      {familyMembersTravellingVisible && (
        <RadioOptionsWithConfirm
          key={Q.FAMILY_MEMBERS_TRAVELLING}
          id={Q.FAMILY_MEMBERS_TRAVELLING}
          value={hasDependents}
          label={'Do you have anyone coming with you to the UK?'}
          inputHelperText={familyMembersTravellingConfig?.helpText}
          onClick={question => {
            handleYesNoClick(Q.FAMILY_MEMBERS_TRAVELLING, question);
          }}
          isInline={true}
          questions={[
            {
              char: 'Y',
              label: 'Yes',
              selected: hasDependents === true
            },
            {
              char: 'N',
              label: 'No',
              selected: hasDependents === false
            }
          ]}
        />
      )}

      {spouseTravellingVisible && (
        <RadioOptionsWithConfirm
          key={Q.SPOUSE_TRAVELLING}
          id={Q.SPOUSE_TRAVELLING}
          value={hasPartner}
          label={'Do you have a partner that is either applying for a UK visa or has a UK visa?'}
          inputHelperText={spouseTravellingConfig?.helpText}
          onClick={question => handleYesNoClick(Q.SPOUSE_TRAVELLING, question)}
          isInline={true}
          questions={[
            {
              char: 'Y',
              label: 'Yes',
              selected: hasPartner === true
            },
            {
              char: 'N',
              label: 'No',
              selected: hasPartner === false
            }
          ]}
        />
      )}

      {partnerVisaStatusVisible && (
        <RadioOptionsWithConfirm
          key={Q.PARTNER_VISA_STATUS}
          id={Q.PARTNER_VISA_STATUS}
          value={partnerVisaStatus}
          label={'My partner is...'}
          inputHelperText={partnerVisaStatusConfig?.helpText}
          onClick={question => handlePartnerVisaStatusClick(question)}
          questions={[
            {
              char: '1',
              label: partnerVisaStatusLabel(types.PartnerVisaStatus.APPLYING_DEPENDANT),
              selected: partnerVisaStatus === types.PartnerVisaStatus.APPLYING_DEPENDANT
            },
            {
              char: '2',
              label: partnerVisaStatusLabel(types.PartnerVisaStatus.APPLYING_DIFFERENT),
              selected: partnerVisaStatus === types.PartnerVisaStatus.APPLYING_DIFFERENT
            },
            {
              char: '3',
              label: partnerVisaStatusLabel(types.PartnerVisaStatus.READY_TO_TRAVEL_VALID),
              selected: partnerVisaStatus === types.PartnerVisaStatus.READY_TO_TRAVEL_VALID
            },
            {
              char: '4',
              label: partnerVisaStatusLabel(types.PartnerVisaStatus.ALREADY_IN_UK_VALID),
              selected: partnerVisaStatus === types.PartnerVisaStatus.ALREADY_IN_UK_VALID
            }
          ]}
        />
      )}

      {childrenTravellingVisible && (
        <RadioOptionsWithConfirm
          key={Q.CHILDREN_TRAVELLING}
          id={Q.CHILDREN_TRAVELLING}
          value={hasChildren}
          label={'Are you applying for your Student visa with dependant children?'}
          onClick={question => {
            handleYesNoClick(Q.CHILDREN_TRAVELLING, question);
            onChange(Q.CHILDREN_TRAVELLING_NUMBER, 0);
          }}
          isInline={true}
          questions={[
            {
              char: 'Y',
              label: 'Yes',
              selected: hasChildren === true
            },
            {
              char: 'N',
              label: 'No',
              selected: hasChildren === false
            }
          ]}
        />
      )}

      {childrenTravellingNumberVisible && (
        <SelectWithConfirm
          key={Q.CHILDREN_TRAVELLING_NUMBER}
          id={Q.CHILDREN_TRAVELLING_NUMBER}
          label="How many children?"
          options={[
            { label: '1', value: '1' },
            { label: '2', value: '2' },
            { label: '3', value: '3' },
            { label: '4', value: '4' },
            { label: '5', value: '5' },
            { label: '6', value: '6' },
            { label: '7', value: '7' },
            { label: '8', value: '8' },
            { label: '9', value: '9' }
          ]}
          value={childrenNum}
          onChange={evt => onChange(Q.CHILDREN_TRAVELLING_NUMBER, Number(evt.currentTarget.value))}
          inputHelperText={childrenTravellingNumberConfig?.helpText}
        />
      )}
    </QuestionPage>
  );
};

export default HistoryQPage3;
